import { Box, Group, Stack } from "@mantine/core"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import useSWR, { mutate } from "swr"
import { DeliveryRouteApi } from "../../apis"
import { IDeliveryRoute } from "../../apis/delivery-route"
import { EditRouteDriverDialog } from "./EditRouteDriverDialog"
import { TablePager } from "../delivery/atoms"
import { ListState } from "../ui"
import { RouteFiltersToolbar, RouteQuickFilter } from "./RouteFiltersToolbar"
import { RouteTable } from "./RouteTable"
import { RouteStatsCards } from "./RouteStatsCards"
import { routeProgress } from "./route-progress"

type RouteStatusFilter = "PLANNED" | "IN_PROGRESS" | "COMPLETED" | null

const PAGE_SIZE = 20

const SUB_TABS: Array<{ value: Exclude<RouteStatusFilter, null>; labelKey: string }> = [
  { value: "PLANNED", labelKey: "delivery:route.filters.tab.PLANNED" },
  { value: "IN_PROGRESS", labelKey: "delivery:route.filters.tab.IN_PROGRESS" },
  { value: "COMPLETED", labelKey: "delivery:route.filters.tab.COMPLETED" },
]


export function DeliveryRoutes() {
  const { t } = useTranslation(["delivery", "common"])
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const statusParam = (searchParams.get("status") as RouteStatusFilter) ?? "PLANNED"
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [quick, setQuick] = useState<RouteQuickFilter>("ALL")
  const [search, setSearch] = useState("")

  const { data, isLoading, error, mutate: mutateRoutes } = useSWR(
    ["delivery-routes.list", statusParam, page, pageSize],
    async () => {
      const result = await DeliveryRouteApi.list({
        filter: statusParam ? { status: statusParam } : {},
        offset: { limit: pageSize, skip: (page - 1) * pageSize },
      })
      return result
    },
    { revalidateOnFocus: false },
  )

  // Memo the array so downstream useMemos don't see a fresh reference
  // on every render (data?.rows ?? [] returns a new [] each call).
  const rows = useMemo(() => data?.rows ?? [], [data?.rows])
  const total = data?.count ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  // Recover from a stale page beyond the result set: if routes were
  // deleted/completed while the user was on page 2+, the fetch returns
  // count>0 with rows=[] (offset past the data), the list shows
  // "Маршрут байхгүй", and the pagination control is hidden — a dead-end
  // that looks like "count says N but list is empty". Clamp back in range.
  useEffect(() => {
    if (!isLoading && page > pageCount) setPage(pageCount)
  }, [isLoading, page, pageCount])

  const { data: statusCountsData } = useSWR(
    ["delivery-routes.status-counts"],
    async () => {
      const statuses: Exclude<RouteStatusFilter, null>[] = ["PLANNED", "IN_PROGRESS", "COMPLETED"]
      const results = await Promise.all(
        statuses.map((s) =>
          DeliveryRouteApi.list({ filter: { status: s }, offset: { limit: 1, skip: 0 } }),
        ),
      )
      return statuses.reduce<Record<string, number>>((acc, s, i) => {
        acc[s] = results[i]?.count ?? 0
        return acc
      }, {})
    },
    { revalidateOnFocus: false },
  )

  const derivedCounts = useMemo(() => {
    const counts = { all: rows.length, waitingDriver: 0, picking: 0, handoverReady: 0, pretripPending: 0 }
    for (const r of rows) {
      const state = routeProgress(r)
      if (state === "WAITING_DRIVER") counts.waitingDriver++
      else if (state === "PICKING") counts.picking++
      else if (state === "HANDOVER_READY") counts.handoverReady++
      else if (state === "PRETRIP_PENDING") counts.pretripPending++
    }
    return counts
  }, [rows])

  const filteredRows = useMemo(() => {
    let r = rows
    if (search.trim()) {
      const needle = search.trim().toLowerCase()
      r = r.filter(
        (route) =>
          route.routeNumber?.toLowerCase().includes(needle) ||
          route.name?.toLowerCase().includes(needle),
      )
    }
    if (quick !== "ALL" && statusParam === "PLANNED") {
      r = r.filter((route) => routeProgress(route) === quick)
    }
    return r
  }, [rows, search, quick, statusParam])

  const handleStatusChange = (next: RouteStatusFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (next) params.set("status", next)
      else params.delete("status")
      return params
    })
    setPage(1)
    setQuick("ALL")
  }

  const [assignTarget, setAssignTarget] = useState<IDeliveryRoute | null>(null)

  const onEditAssign = (route: IDeliveryRoute) => setAssignTarget(route)
  const onStartHandover = (route: IDeliveryRoute) =>
    navigate(`/supplier-delivery?tab=HANDOVER&routeId=${encodeURIComponent(route.id)}`)

  return (
    <Stack gap="md">
      <RouteFiltersToolbar
        status={statusParam ?? "PLANNED"}
        onStatusChange={(v) => handleStatusChange(v as RouteStatusFilter)}
        statusOptions={SUB_TABS.map((s) => ({ value: s.value, label: t(s.labelKey) }))}
        statusCounts={statusCountsData}
        search={search}
        onSearchChange={setSearch}
      />

      {/* Loading stages only exist while a route is planned; the tiles double as the stage filter. */}
      {statusParam === "PLANNED" && <RouteStatsCards counts={derivedCounts} value={quick} onChange={setQuick} />}

      {/* Body: one table (the card list was dropped, 2026-11-04). */}
      <Box mih={200}>
        {isLoading ? (
          <ListState state="loading" columns={5} />
        ) : error ? (
          <ListState
            state="error"
            title={t("delivery:route.list.loadError")}
            description={t("delivery:route.list.loadErrorText")}
            onRetry={() => mutateRoutes()}
          />
        ) : filteredRows.length === 0 ? (
          <ListState
            state={search.trim() || quick !== "ALL" ? "noResults" : "empty"}
            title={t("delivery:route.list.empty")}
            action={
              search.trim() || quick !== "ALL"
                ? { onClick: () => { setSearch(""); setQuick("ALL") } }
                : undefined
            }
          />
        ) : (
          <RouteTable rows={filteredRows} onEdit={onEditAssign} onStartHandover={onStartHandover} />
        )}
      </Box>

      {total > 0 && (
        <Group justify="space-between" wrap="wrap" gap="sm">
          <TablePager page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
        </Group>
      )}

      {assignTarget && (
        <EditRouteDriverDialog
          opened={!!assignTarget}
          onClose={() => setAssignTarget(null)}
          route={assignTarget}
          onSuccess={() => {
            mutateRoutes()
            mutate((key) => Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith("delivery-routes"))
          }}
        />
      )}
    </Stack>
  )
}
